import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import StatusChip from "../../components/StatusChip";
import CompactPreview from "../../components/CompactPreview";

import { tokens } from "../../theme/tokens";
import { graphqlEndpoint } from "../../config";
import { sopStatusTone } from "../../status/StatusChipTones";

import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useResolvedTenantId } from "../../hooks/useResolvedTenantId";

import { READ_SOP_LIBRARY_FILTER_DATA } from "../../graph/projects/queries";

const client = new GraphQLClient(graphqlEndpoint);

const sopStatusLabel = (status) => {
  const labels = {
    ACTIVE: "Active",
    APPROVED: "Approved",
    PENDING: "Pending",
    PENDING_APPROVAL: "Pending approval",
    IN_REVIEW: "In review",
    DRAFT: "Draft",
    REJECTED: "Rejected",
    ARCHIVED: "Archived",
  };

  return labels[status] || status || "SOP";
};

export default function MySops() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const { setSnackbar } = useSnackbar();

  const {
    user,
    tenantId,
    tenantName,
  } = useResolvedTenantId();

  const loggedInUserId = user?.id;

  const [filterData, setFilterData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMySopsData = async () => {
      if (!tenantId || !loggedInUserId) {
        setFilterData(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const {
          readRoleAllocationBoard: mySopsData,
        } = await client.request(
          READ_SOP_LIBRARY_FILTER_DATA,
          {
            input: {
              tenantId,
            },
          }
        );

        setFilterData(mySopsData);
      } catch (err) {
        console.error("Failed to load my SOPs:", err);

        setFilterData(null);

        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load your SOPs",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMySopsData();
  }, [
    tenantId,
    loggedInUserId,
    setSnackbar,
  ]);

  const mySops = useMemo(() => {
    const roles = filterData?.roles || [];
    const departments =
      filterData?.departments || [];

    const sopMap = new Map();

    roles.forEach((roleItem) => {
      const isAssignedToUser =
        roleItem.peopleCards?.some(
          (person) =>
            person.tenantUser?.userId ===
              loggedInUserId ||
            person.tenantUser?.id === loggedInUserId
        );

      if (!isAssignedToUser) {
        return;
      }

      const role = roleItem.role;

      const department = departments.find(
        (item) => item.id === role?.departmentId
      );

      roleItem.sopCards?.forEach((card) => {
        if (!card.sop?.id) {
          return;
        }

        const isVisibleToStaff =
        card.sop?.approvalStatus === "APPROVED";

        if (!isVisibleToStaff) {
          return;
        }

        const existing = sopMap.get(card.sop.id);

        const existingRoleNames =
          existing?.roleNames || [];

        const existingDepartmentNames =
          existing?.departmentNames || [];

        sopMap.set(card.sop.id, {
          ...card.sop,
          roleNames: [
            ...new Set(
              [
                ...existingRoleNames,
                role?.name,
              ].filter(Boolean)
            ),
          ],
          departmentNames: [
            ...new Set(
              [
                ...existingDepartmentNames,
                department?.name,
              ].filter(Boolean)
            ),
          ],
        });
      });
    });

    return Array.from(sopMap.values());
  }, [filterData, loggedInUserId]);

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="MY SOPs"
          subtitle="Tenant could not be resolved"
        />

        <Typography mt={3}>
          A tenant must be available before viewing your
          SOPs.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <Header
          title="MY SOPs"
          subtitle="Loading your assigned SOPs"
        />

        <Typography mt={3}>
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Header
        title="MY SOPs"
        subtitle={
          tenantName
            ? `SOPs assigned to your roles at ${tenantName}`
            : "SOPs assigned to your roles"
        }
      />

      {mySops.length === 0 ? (
        <Box mt={3}>
          <Typography variant="h6">
            No SOPs assigned yet
          </Typography>

          <Typography
            color="text.secondary"
            mt={1}
          >
            SOPs will appear here once your role has SOPs
            assigned to it.
          </Typography>
        </Box>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(auto-fill, minmax(320px, 1fr))"
          alignItems="start"
          mt={2}
        >
          {mySops.map((sop) => {
            const total = sop.tasks?.length || 0;

            const done = (sop.tasks || []).filter(
              (task) => task.status === "DONE"
            ).length;

            const progressPercent = total
              ? Math.round((done / total) * 100)
              : 0;

            return (
              <Paper
                key={sop.id}
                sx={{
                  p: 2,
                  border: `1px solid ${colors.scalpelTeal[400]}`,
                  bgcolor: "background.paper",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ mb: 0.5 }}
                >
                  {sop.title}
                </Typography>

                {sop.departmentNames?.length > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      mb: 1,
                    }}
                  >
                    Department:{" "}
                    {sop.departmentNames.join(", ")}
                  </Typography>
                )}

                {sop.roleNames?.length > 0 && (
                  <Box
                    sx={{
                      mb: 1.5,
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {sop.roleNames.map((roleName) => (
                      <StatusChip
                        key={roleName}
                        label={roleName}
                        tone="teal"
                      />
                    ))}
                  </Box>
                )}

                <CompactPreview lines={8}>
                    {sop.description || "No description"}
                  </CompactPreview>

                <Box sx={{ mb: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progressPercent}
                  />

                  <Typography variant="caption">
                    {done}/{total} steps completed
                  </Typography>
                </Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={1}
                  gap={1}
                >
                  <StatusChip
                    label={sopStatusLabel(sop.status)}
                    tone={sopStatusTone(sop.status)}
                  />

                  <ActionButton
                    tone="success"
                    onClick={() =>
                      navigate(`/sops/read/${sop.id}`)
                    }
                  >
                    Read SOP
                  </ActionButton>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}